import { Dropdown } from '@florencelabs/dropdown';
import { MaterialSymbol } from '@florencelabs/icons';
import {
  ListBox,
  ListBoxItem as SelectorItem,
  ListBoxSection as SelectorSection,
  ListBoxItemContent as SelectorItemContent,
  type ListBoxSectionProps as SelectorSectionProps,
  type ListBoxItemProps as SelectorItemProps,
} from '@florencelabs/list-box';
import { AriaSelectProps } from '@react-types/select';
import clsx from 'clsx';
import React, { useRef, useContext, ReactElement, useState } from 'react';
import {
  Button,
  Label,
  FieldError,
  Select as ReactAriaSelect,
  SelectValue,
  ValidationResult,
  SelectStateContext,
} from 'react-aria-components';
import { useMediaQuery } from 'react-responsive';
import { cn } from '@provider-directory/functions/cn';

import './selector.css';

const SelectClearButton = (): ReactElement => {
  const state = useContext(SelectStateContext);
  return (
    <Button
      className={clsx('selector-clear-button', {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        hidden: state?.selectedKey === null,
      })}
      slot={null}
      onPress={() => {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        state?.setSelectedKey(null);
      }}
    >
      <MaterialSymbol name={'close_small'} />
    </Button>
  );
};

const SelectorLabel = <T extends object>({
  label,
  placeholder,
  isRequired,
  optionalText,
}: Pick<
  SelectorProps<T>,
  'label' | 'placeholder' | 'isRequired' | 'optionalText'
>): ReactElement => {
  const state = useContext(SelectStateContext);

  return (
    <Label className={clsx('selector-label')}>
      <span className={'selector-label-text'}>{label}</span>
      {!isRequired && optionalText ? (
        <span
          className={clsx(
            'selector-label-helper-text',
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            placeholder || state?.selectedKey ? 'pl-xs' : 'pl-s',
          )}
        >
          {optionalText}
        </span>
      ) : null}
    </Label>
  );
};

const SelectValueBody = <T extends object>({
  placeholder,
  clearable,
  isDisabled,
}: Pick<
  SelectorProps<T>,
  'placeholder' | 'clearable' | 'isDisabled'
>): ReactElement => {
  const state = useContext(SelectStateContext);

  return (
    <>
      <SelectValue
        data-testid={'open-list'}
        className={clsx('selector-value max-w-[calc(100%-3rem)]', {
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          hidden: !placeholder && !state?.selectedKey,
        })}
      >
        {({ isPlaceholder, defaultChildren }) => {
          return isPlaceholder ? placeholder : defaultChildren;
        }}
      </SelectValue>
      {!isDisabled && (
        <MaterialSymbol
          name={state?.isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          className={clsx(
            'pointer-events-none absolute right-4 top-1/2 -translate-y-1/2',
            {
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              '!hidden': clearable && state?.selectedKey,
            },
          )}
          aria-hidden={true}
        />
      )}
    </>
  );
};

export interface SelectorProps<T extends object>
  extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string;
  labelPlacement?: 'inner' | 'outer';
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  className?: string;
  buttonClassName?: string;
  clearable?: boolean;
  optionalText?: string;
  maxHeight?: number;
  testIdPrefix?: string | null;
  isDisabled?: boolean;
}
export const useIsMobileOrTablet = (): boolean =>
  useMediaQuery({ maxWidth: 1199 });

export const Selector = <T extends object>({
  name,
  children,
  placeholder,
  className,
  label,
  clearable = true,
  isRequired = false,
  optionalText,
  errorMessage,
  maxHeight = 260,
  isInvalid = false,
  testIdPrefix = null,
  isDisabled,
  buttonClassName,
  ...props
}: SelectorProps<T>): ReactElement => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    // TODO Type '{ children: ("" | Element | null | undefined)[]; isDisabled: boolean | undefined; isInvalid: boolean; className: string; description?: string | undefined; items?: Iterable<T> | undefined; ... 22 more ...; excludeFromTabOrder?: boolean | undefined; }' is not assignable to type 'SelectProps<{}>'.
    // @ts-ignore
    <ReactAriaSelect
      {...props}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      onBlur={() => {
        setIsOpen(false);
      }}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      className={cn('selector', className)}
    >
      <SelectorLabel
        label={label}
        placeholder={placeholder}
        isRequired={isRequired}
        optionalText={optionalText}
      />
      <Button
        data-testid={testIdPrefix ? `${testIdPrefix}-select-${name}` : null}
        ref={triggerRef}
        className={({ isFocused }) =>
          clsx(
            'selector-button',
            isInvalid && '!border-red-600',
            isFocused && '!border-gray-400',
            buttonClassName,
          )
        }
      >
        <SelectValueBody
          isDisabled={isDisabled}
          clearable={clearable}
          placeholder={placeholder}
        />
      </Button>
      {clearable ? <SelectClearButton /> : null}
      <FieldError
        data-testid={
          testIdPrefix ? `${testIdPrefix}-select-${name}-error` : null
        }
        className={'pt-xs text-body-small-medium text-red-600'}
      >
        {errorMessage}
      </FieldError>
      <Dropdown
        triggerRef={triggerRef}
        className={isInvalid ? '!border-red-600' : ''}
        maxHeight={maxHeight}
        // Controls if the dropdown closes on scroll OR behaves like a modal and locks scroll when open
        // Since this is a dropdown, we want to allow scrolling and close on scroll
        isNonModal={true}
      >
        <ListBox className={'selector-listbox'}>{children}</ListBox>
      </Dropdown>
    </ReactAriaSelect>
  );
};

export {
  SelectorItem,
  SelectorItemContent,
  SelectorSection,
  SelectorSectionProps,
  type SelectorItemProps,
};
